/**
 * Keep capability tokens out of telemetry (T-144 PR#3b review fix).
 *
 * The document-share link puts a 32-byte hex capability token in the URL path
 * (/share/:token, /api/v1/public/share/:token/...). That token is a bearer
 * secret — it must never land in analytics, error events, traces, breadcrumbs,
 * or session replay. These helpers redact any 32+ hex run from URL-shaped
 * strings and are shared by the Sentry init (instrument.js) and the PostHog
 * provider so there is one audited redactor.
 */
export const TOKEN_RUN = /[0-9a-f]{32,}/gi;

// P7 handoff (docs/auth/handoff-contract.md §5): the one-time sign-in code rides
// in /auth/handoff#h=<code>. The page clears it before telemetry starts; this is
// the backstop, and it drops the whole fragment, whatever its shape.
const HANDOFF_FRAGMENT = /(\/auth\/handoff\/?)#[^\s"'<>]*/gi;

// Alert email links (Eric, 2026-10-28): the signed action link rides in
// /alert-action#t=<token>. The page moves it out of the address bar on load; this
// is the backstop, dropping the whole fragment on that route.
const ALERT_ACTION_FRAGMENT = /(\/alert-action\/?)#[^\s"'<>]*/gi;

// T-207 request-for-access links: /portal/access-request#t=<token>. index.html moves it out of the
// address bar on load; a second link opened in the same tab is only a fragment change, so this is
// the backstop (review on #1080).
const PORTAL_ACCESS_FRAGMENT = /(\/portal\/access-request\/?)#[^\s"'<>]*/gi;

// T-220 "please upload this" links: /portal/task#t=<token>. index.html moves it out of the address
// bar on load and the page takes a second link opened in the same tab; this is the backstop.
const PORTAL_TASK_FRAGMENT = /(\/portal\/task\/?)#[^\s"'<>]*/gi;

// Signed storage links (review on #1174): an upload or download link carries its one-time JWT in a
// `token` query parameter (base64url, so TOKEN_RUN never matches it). A fetch breadcrumb would
// otherwise hold the whole link.
const TOKEN_QUERY = /([?&]token=)[^&#\s"'<>]+/gi;

/** Replace any capability-token-length hex run in a string with [redacted]. */
export function scrubTokenString<T>(value: T): T {
  return typeof value === 'string'
    ? (value
      .replace(HANDOFF_FRAGMENT, '$1')
      .replace(ALERT_ACTION_FRAGMENT, '$1')
      .replace(PORTAL_ACCESS_FRAGMENT, '$1')
      .replace(PORTAL_TASK_FRAGMENT, '$1')
      .replace(TOKEN_QUERY, '$1[redacted]')
      .replace(TOKEN_RUN, '[redacted]') as unknown as T)
    : value;
}

const URL_PROP_KEYS = [
  '$current_url',
  '$pathname',
  '$referrer',
  '$initial_current_url',
  '$initial_pathname',
  '$initial_referrer',
  // PostHog adds the session's entry URL to every event of the session (PR #1064 review).
  '$session_entry_url',
  '$session_entry_referrer',
];

/** Redact tokens from the URL-valued keys of a PostHog properties object. */
export function scrubTokenUrlProps<T extends Record<string, unknown> | null>(props: T): T {
  if (!props) return props;
  const p = props as Record<string, unknown>;
  for (const key of URL_PROP_KEYS) {
    if (typeof p[key] === 'string') p[key] = scrubTokenString(p[key] as string);
  }
  return props;
}

type SentryBreadcrumbLike = { message?: unknown; data?: Record<string, unknown> } | null | undefined;
type SentryEventLike =
  | {
      request?: { url?: unknown };
      transaction?: unknown;
      breadcrumbs?: SentryBreadcrumbLike[];
    }
  | null
  | undefined;

/** Redact tokens from a Sentry breadcrumb's url/message in place. */
export function scrubTokenBreadcrumb<T extends SentryBreadcrumbLike>(crumb: T): T {
  if (!crumb) return crumb;
  if (crumb.data && typeof crumb.data.url === 'string') crumb.data.url = scrubTokenString(crumb.data.url);
  // Navigation breadcrumbs carry from/to (PR #1064 review).
  if (crumb.data && typeof crumb.data.from === 'string') crumb.data.from = scrubTokenString(crumb.data.from);
  if (crumb.data && typeof crumb.data.to === 'string') crumb.data.to = scrubTokenString(crumb.data.to);
  if (typeof crumb.message === 'string') crumb.message = scrubTokenString(crumb.message);
  return crumb;
}

/** Redact tokens from a Sentry event's request URL, transaction name, and breadcrumbs. */
export function scrubTokenSentryEvent<T extends SentryEventLike>(event: T): T {
  if (!event) return event;
  if (event.request && typeof event.request.url === 'string') event.request.url = scrubTokenString(event.request.url);
  if (typeof event.transaction === 'string') event.transaction = scrubTokenString(event.transaction);
  if (Array.isArray(event.breadcrumbs)) event.breadcrumbs.forEach(scrubTokenBreadcrumb);
  return event;
}
